import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Typography } from '../../primitives/Typography';
import { mediaQuery } from '../../tokens/breakpoints';
import { rem } from '../../tokens/spacing';

export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  maxWidth?: string;
  padding?: string;
  semanticId?: string;
  /** Content rendered above the title (e.g. BackLink). */
  beforeTitle?: React.ReactNode;
  /** Page heading rendered above children. */
  title?: React.ReactNode;
  /** Muted subtitle rendered below the title. */
  subtitle?: React.ReactNode;
}

const Root = styled.div`
  box-sizing: border-box;
  border-radius: var(--ui-radius-lg, ${rem(10)});
  padding: ${rem(20)};
  width: 100%;
  position: relative;

  ${mediaQuery.md} {
    padding: ${rem(37)};
  }

  @media (min-width: 1200px) {
    padding: ${rem(43)} ${rem(120)};
  }
`;

const Inner = styled.div<{
  $maxWidth: string;
}>`
  box-sizing: border-box;
  max-width: ${({ $maxWidth }) => $maxWidth};
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const Header = styled.div`
  margin-bottom: ${rem(24)};
`;

export const Page = React.forwardRef<HTMLDivElement, PageProps>(function Page(
  {
    maxWidth = '100%',
    semanticId,
    beforeTitle,
    title,
    subtitle,
    children,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <Root ref={ref} {...semanticProps} {...rest}>
      <Inner $maxWidth={maxWidth}>
        {beforeTitle}
        {(title != null || subtitle != null) && (
          <Header>
            {title != null && <Typography variant='h3'>{title}</Typography>}
            {subtitle != null && (
              <Typography variant='body-s' color='var(--ui-text-muted)'>
                {subtitle}
              </Typography>
            )}
          </Header>
        )}
        {children}
      </Inner>
    </Root>
  );
});

Page.displayName = 'Page';
